import { useCallback, useMemo } from 'react';

import { getSeriesId } from '@/features/charts/lib/seriesVisibility';
import type { ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';

const useLegendDropdown = ({
  series,
  hiddenSeriesIds,
  setHiddenSeriesIds
}: {
  series: ExtendedSeriesOptionsType[];
  hiddenSeriesIds: string[];
  setHiddenSeriesIds: (ids: string[]) => void;
}) => {
  const allChartSeries = useMemo(
    () => series.filter(item => !item.name?.includes('Navigator')),
    [series]
  );
  const allSeriesIds = useMemo(
    () => allChartSeries.map(item => getSeriesId(item)).filter((id): id is string => Boolean(id)),
    [allChartSeries]
  );
  const selectedSeriesIds = useMemo(() => {
    const visibleIds = allSeriesIds.filter(id => !hiddenSeriesIds.includes(id));
    return visibleIds.length > 0 ? visibleIds : allSeriesIds;
  }, [allSeriesIds, hiddenSeriesIds]);
  const selectedSeries = useMemo(
    () => allChartSeries.filter(item => selectedSeriesIds.includes(getSeriesId(item) ?? '')),
    [allChartSeries, selectedSeriesIds]
  );

  const handleLegendItemClick = useCallback(
    (seriesId: string, checked: boolean) => {
      if (!allSeriesIds.includes(seriesId)) return;
      if (!checked && selectedSeriesIds.length === 1 && selectedSeriesIds[0] === seriesId) return;

      const nextSelectedIds = checked
        ? Array.from(new Set([...selectedSeriesIds, seriesId]))
        : selectedSeriesIds.filter(id => id !== seriesId);
      if (nextSelectedIds.length === 0) return;

      const nextHiddenIds = allSeriesIds.filter(id => !nextSelectedIds.includes(id));
      setHiddenSeriesIds(nextHiddenIds);
    },
    [allSeriesIds, selectedSeriesIds, setHiddenSeriesIds]
  );

  const isSeriesDisabled = useCallback(
    (item: ExtendedSeriesOptionsType) => {
      const seriesId = getSeriesId(item);
      return selectedSeriesIds.length === 1 && seriesId === selectedSeriesIds[0];
    },
    [selectedSeriesIds]
  );

  return {
    selectedSeries,
    selectedSeriesIds,
    allChartSeries,
    handleLegendItemClick,
    isSeriesDisabled
  };
};

export default useLegendDropdown;
